import type { Module } from 'vuex';
import { Currency } from '@/types/Currency';
import { CurrencyBlockchain } from "@/types/CurrencyBlockchain";
import {RootState} from "@/store";

export interface CurrencyState {
  currency: Currency | null;
  deleted: boolean;
}

const currencyModule: Module<CurrencyState, RootState> = {
  namespaced: true,

  state: (): CurrencyState => ({
    currency: null,
    deleted: false,
  }),

  mutations: {
    setCurrency(state, payload: { idCurrency: number; code: string; name: string | null; description: string | null; locked: boolean; blockchains?: any[] }) {
      const raw = {
        idCurrency: payload.idCurrency,
        code: payload.code,
        name: payload.name,
        description: payload.description,
        locked: payload.locked,
        blockchains: Array.isArray(payload.blockchains) ? payload.blockchains : [],
      };
      state.currency = Currency.fromRaw(raw);
      state.deleted = false;
    },

    setCurrencyBlockchains(
      state,
      payload: { currencyBlockchains: Record<string, Record<string, any>> }
    ) {
      if (!state.currency) return;
      state.currency.blockchains = [];
      if (payload.currencyBlockchains) {
        const currencyBlockchains = payload.currencyBlockchains[state.currency.idCurrency];
        for (const idBlockchain in currencyBlockchains) {
          const raw = currencyBlockchains[idBlockchain];
          state.currency.blockchains.push(CurrencyBlockchain.fromRaw(raw));
        }
        }
    },

    setCurrencyBlockchainCreate(
      state,
      payload: {
        idCurrency: number;
        idBlockchain: number;
        contract: string | null;
        main: boolean;
        locked: boolean;
      }
    ) {
      if (!state.currency || state.currency.idCurrency !== payload.idCurrency) return;
      const raw = {
        idCurrency: payload.idCurrency,
        idBlockchain: payload.idBlockchain,
        contract: payload.contract,
        main: payload.main,
        locked: payload.locked,
      };
      state.currency.blockchains.push(CurrencyBlockchain.fromRaw(raw));
    },

    setCurrencyUpdate(state, payload: { idCurrency: number; code?: string; name?: string | null; description?: string | null }) {
      if (!state.currency || state.currency.idCurrency !== payload.idCurrency) return;
      if (payload.code !== undefined)  state.currency.code   = payload.code;
      if (payload.name !== undefined)  state.currency.name   = payload.name;
      if (payload.description !== undefined) state.currency.description = payload.description;
    },

    setCurrencyDelete(state, payload: { idCurrency: number }) {
      if (state.currency?.idCurrency === payload.idCurrency) {
        state.deleted = true;
      }
    },

    setCurrencyLock(state, payload: { idCurrency: number }) {
      if (state.currency?.idCurrency === payload.idCurrency) {
        state.currency.locked = true;
      }
    },

    setCurrencyUnlock(state, payload: { idCurrency: number }) {
      if (state.currency?.idCurrency === payload.idCurrency) {
        state.currency.locked = false;
      }
    },

    setCurrencyBlockchainUpdate(
      state,
      payload: {
        idCurrency: number;
        idBlockchain: number;
        contract?: string;
        main?: boolean;
        locked?: boolean;
      }
    ) {
      if (!state.currency || state.currency.idCurrency !== payload.idCurrency) return;
      const currencyBlockchain = state.currency.blockchains.find(
        cb => cb.idBlockchain === payload.idBlockchain
      );
      if (!currencyBlockchain) return;
      if (payload.contract !== undefined) currencyBlockchain.contract = payload.contract;
      if (payload.main !== undefined) currencyBlockchain.main = payload.main;
      if (payload.locked !== undefined) currencyBlockchain.locked = payload.locked;
    },

    setCurrencyBlockchainDelete(
      state,
      payload: { idCurrency: number, idBlockchain: number }
    ) {
      if (!state.currency || state.currency.idCurrency !== payload.idCurrency) return;
      state.currency.blockchains = state.currency.blockchains.filter(
        cb => cb.idBlockchain !== payload.idBlockchain
      );
    },

    setCurrencyBlockchainLock(
      state,
      payload: { idCurrency: number, idBlockchain: number }
    ) {
      if (!state.currency || state.currency.idCurrency !== payload.idCurrency) return;
      const currencyBlockchain = state.currency.blockchains.find(
        cb => cb.idBlockchain === payload.idBlockchain
      );
      if (currencyBlockchain) currencyBlockchain.locked = true;
    },

    setCurrencyBlockchainUnlock(
      state,
      payload: { idCurrency: number, idBlockchain: number }
    ) {
      if (!state.currency || state.currency.idCurrency !== payload.idCurrency) return;
      const currencyBlockchain = state.currency.blockchains.find(
        cb => cb.idBlockchain === payload.idBlockchain
      );
      if (currencyBlockchain) currencyBlockchain.locked = false;
    },

    clearCurrency(state) {
      state.currency = null;
      state.deleted = false;
    },
  },

  actions: {
    wsSetCurrency({ commit }, payload) {
      commit('setCurrency', payload);
    },
    wsSetEmptyCurrency({ commit }) {
      commit('clearCurrency');
    },
    wsSetCurrencyBlockchains({ commit }, payload) {
      commit('setCurrencyBlockchains', payload);
    },
    wsCurrencyUpdate({ commit }, payload) {
      commit('setCurrencyUpdate', payload);
    },
    wsCurrencyDelete({ commit }, payload) {
      commit('setCurrencyDelete', payload);
    },
    wsCurrencyLock({ commit }, payload) {
      commit('setCurrencyLock', payload);
    },
    wsCurrencyUnlock({ commit }, payload) {
      commit('setCurrencyUnlock', payload);
    },
    wsCurrencyBlockchainCreate({ commit }, payload) {
      commit('setCurrencyBlockchainCreate', payload);
    },
    wsCurrencyBlockchainUpdate({ commit }, payload) {
      commit('setCurrencyBlockchainUpdate', payload);
    },
    wsCurrencyBlockchainDelete({ commit }, payload) {
      commit('setCurrencyBlockchainDelete', payload);
    },
    wsCurrencyBlockchainLock({ commit }, payload) {
      commit('setCurrencyBlockchainLock', payload);
    },
    wsCurrencyBlockchainUnlock({ commit }, payload) {
      commit('setCurrencyBlockchainUnlock', payload);
    },
    clearCurrency({ commit }) {
      commit('clearCurrency');
    },
  },
};

export default currencyModule;
